import React, { useEffect, useState } from "react";
import api from "../../services/api/axios";
import { useAuth } from "../../providers/AuthProvider";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Stack,
  Button,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import BusinessIcon from "@mui/icons-material/Business";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AddIcon from "@mui/icons-material/Add";
import PersonAddIcon from "@mui/icons-material/PersonAdd";

import DashboardSignalRow from "./DashboardSignalRow";

const BRAND_BLUE = "#00336C";
const BRAND_GREEN = "#648315";

const CARD_SX = {
  height: "100%",
  border: "1px solid rgba(0,51,108,0.12)",
  borderRadius: "12px",
  boxShadow: "0 1px 3px rgba(15,23,42,0.05), 0 8px 20px rgba(15,23,42,0.06)",
  background: "#ffffff",
  overflow: "hidden",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "3px",
    background: `linear-gradient(90deg, ${BRAND_BLUE} 0%, #1d6fa8 100%)`,
  },
};

const ACTION_BTN_SX = {
  minHeight: 34,
  px: 1.25,
  borderRadius: "8px",
  border: "1.5px solid rgba(0,51,108,0.18)",
  color: BRAND_BLUE,
  backgroundColor: "#eef2f8",
  textTransform: "none",
  fontWeight: 600,
  fontSize: 12.5,
  gap: 0.5,
  transition: "all 0.15s ease",
  "&:hover": {
    backgroundColor: "#e2eaf5",
    borderColor: "rgba(0,51,108,0.4)",
  },
};

const PRIMARY_ACTION_BTN_SX = {
  ...ACTION_BTN_SX,
  border: `1.5px solid ${BRAND_GREEN}`,
  color: "#ffffff",
  backgroundColor: BRAND_GREEN,
  boxShadow: "0 2px 8px rgba(100,131,21,0.2)",
  "&:hover": {
    backgroundColor: "#536e12",
    borderColor: "#536e12",
    boxShadow: "0 4px 12px rgba(100,131,21,0.24)",
  },
};

function BREHomeSummary() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [breSummary, setBreSummary] = useState(null);

  useEffect(() => {
    if (!user) return;
    api
      .get("/pipeline/summary")
      .then((res) => setBreSummary(res.data))
      .catch(() => setBreSummary(null));
  }, [user]);

  return (
    <Card sx={CARD_SX}>
      <CardContent
        sx={{
          p: 2.75,
          pt: 3,
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        {/* Card Header */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.25 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "12px",
              background: BRAND_GREEN,
              color: "#fff",
              display: "grid",
              placeItems: "center",
              flex: "0 0 auto",
              boxShadow: `0 2px 8px ${BRAND_GREEN}40`,
            }}
          >
            <BusinessIcon sx={{ fontSize: 24 }} />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                color: BRAND_BLUE,
                fontSize: 17,
                fontWeight: 800,
                lineHeight: 1.15,
              }}
            >
              Business Retention &amp; Expansion
            </Typography>
            <Typography
              sx={{
                color: "#5c718f",
                fontSize: 12.5,
                mt: 0.35,
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 600,
              }}
            >
              Key Activity &amp; Alerts
            </Typography>
          </Box>
          <Box
            sx={{
              px: 1,
              py: 0.35,
              borderRadius: "20px",
              backgroundColor: "rgba(100,131,21,0.08)",
              border: "1px solid rgba(100,131,21,0.18)",
              flexShrink: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 800,
                color: BRAND_GREEN,
                letterSpacing: 0.7,
                textTransform: "uppercase",
              }}
            >
              BR&amp;E
            </Typography>
          </Box>
        </Box>

        {/* Signal Rows */}
        <Stack spacing={0.85} sx={{ flex: 1 }}>
          <DashboardSignalRow
            icon={<BusinessIcon />}
            label="Companies Needing Outreach"
            value={breSummary?.companies_needing_outreach ?? 0}
            color="#B8860B"
            priority
            zeroText="All caught up"
            tooltip="Companies in your BR&E pipeline that still need an outreach touchpoint or follow-up."
            onClick={() =>
              navigate("/bre-companies?pipeline_stage=Needs%20Outreach")
            }
          />
          <DashboardSignalRow
            icon={<SupportAgentIcon />}
            label="Support In Progress"
            value={breSummary?.support_in_progress_companies ?? 0}
            color={BRAND_GREEN}
            tooltip="Companies currently receiving active assistance, issue resolution, or ongoing BR&E support."
            onClick={() =>
              navigate("/bre-companies?pipeline_stage=Support%20In%20Progress")
            }
          />
          <DashboardSignalRow
            icon={<WarningAmberIcon />}
            label="Companies Flagged At Risk"
            value={breSummary?.at_risk_companies ?? 0}
            color="#C62828"
            priority
            zeroText="No companies currently at risk"
            tooltip="Companies marked as at risk due to retention concerns, operational issues, or warning signs."
            onClick={() => navigate("/bre-companies?pipeline_stage=At%20Risk")}
          />
          <DashboardSignalRow
            icon={<FactCheckIcon />}
            label="New Surveys Completed"
            value={breSummary?.new_surveys_completed ?? 0}
            color="#00838F"
            tooltip="Recently completed BR&E survey responses that are available for review."
            onClick={() => navigate("/survey-responses")}
          />
          <DashboardSignalRow
            icon={<EventAvailableIcon />}
            label="Leases Expiring In Next 6 Months"
            value={breSummary?.lease_expiring_next_6_months ?? 0}
            color="#ED6C02"
            zeroText="No upcoming expirations"
            tooltip="Companies with lease expiration dates coming up within the next six months."
            onClick={() =>
              navigate("/bre-companies?lease_expiring=next_6_months")
            }
          />
        </Stack>

        <Divider sx={{ my: 2, borderColor: "rgba(0,51,108,0.07)" }} />

        {/* Quick Actions */}
        <Box>
          <Typography
            sx={{
              fontSize: 10.5,
              fontWeight: 700,
              color: "#7b90b2",
              letterSpacing: 1.1,
              textTransform: "uppercase",
              mb: 1,
            }}
          >
            Quick Actions
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={0.75}>
            <Button
              variant="contained"
              startIcon={<AddIcon sx={{ fontSize: "14px !important" }} />}
              onClick={() => navigate("/bre-companies/new")}
              sx={{ ...PRIMARY_ACTION_BTN_SX, flex: 1.1 }}
            >
              Add Company
            </Button>
            <Button
              variant="outlined"
              startIcon={<PersonAddIcon sx={{ fontSize: "14px !important" }} />}
              onClick={() => navigate("/bre-contacts/new")}
              sx={{ ...ACTION_BTN_SX, flex: 1 }}
            >
              Add Contact
            </Button>
            <Button
              variant="outlined"
              startIcon={
                <ArrowForwardIcon sx={{ fontSize: "14px !important" }} />
              }
              onClick={() => navigate("/bre-companies")}
              sx={{ ...ACTION_BTN_SX, flex: 1 }}
            >
              View All
            </Button>
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
}

export default BREHomeSummary;
